"use client";

import { cn } from "@yardzen/ui/lib/utils";

export interface SelectorPillOption {
  /** Stable identity for the option. Used as the React key. */
  id: string;
  label: string;
}

/**
 * The segmented pill row a Contentful section uses to switch between its own
 * items: the yard tabs on Plan Comparison, the project tabs on Before & After.
 *
 * It was written twice, and the two drifted apart — one was a rounded-xl pill
 * on the `scheme-pill-*` roles, the other a rounded-lg div on raw brand hexes
 * that no scheme could repaint. This is one control now, on scheme roles, so a
 * partner restyles both from `schemes.js`.
 *
 * Colour comes from the `scheme-pill-*` roles, so a partner scheme restyles the
 * control from `schemes.js` — `toll-brothers` paints the selected pill in Toll
 * blue, and did so before this component existed. Weight carries the state as
 * well as fill, because the default pair is deliberately quiet.
 *
 * Each pill is a real `<button>`, and the row is a `tablist`, so the set is
 * reachable and announced rather than being a div a mouse happens to hit.
 */
export function SelectorPills({
  options,
  selectedIndex,
  onSelect,
  label,
  className,
}: {
  options: SelectorPillOption[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  /** Names the group for assistive tech, e.g. "Before and after projects". */
  label: string;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn(
        // Scrollable at every breakpoint. `md:w-auto md:overflow-hidden` came
        // from Plan Comparison, where the count is capped at four; a
        // before-and-after section has no cap, and six mono labels already
        // overflow a 900px column with nothing to scroll.
        "flex max-w-full flex-row gap-1.5 overflow-x-auto px-0 md:mx-0 md:w-auto md:gap-2 md:px-4",
        className,
      )}
    >
      {options.map((option, index) => (
        <button
          key={option.id}
          type="button"
          role="tab"
          aria-selected={selectedIndex === index}
          onClick={() => onSelect(index)}
          className={cn(
            "min-w-28 flex-shrink-0 rounded-xl px-4 py-2 transition-colors",
            "font-mono text-xs uppercase tracking-wider",
            "focus-visible:ring-scheme-action focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
            selectedIndex === index
              ? "bg-scheme-pill-selected-bg text-scheme-pill-selected-text font-semibold"
              : "bg-scheme-pill-bg/50 text-scheme-pill-text hover:bg-scheme-pill-bg font-normal",
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
